'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { createChapter, createScene } from '@/lib/actions';

interface Scene {
  id: number;
  title: string;
  chapter_id: number;
  order_index: number;
}

interface Chapter {
  id: number;
  title: string;
  arc_id: number;
  order_index: number;
  scenes: Scene[];
}

interface Arc {
  id: number;
  title: string;
  summary?: string;
  chapters: Chapter[];
}

interface Props {
  arc: Arc;
  selectedSceneId?: string;
  arcId: string;
}

export default function StoryboardTree({ arc, selectedSceneId, arcId }: Props) {
  const router = useRouter();

  const [expandedChapters, setExpandedChapters] = useState<Set<number>>(
    new Set(arc.chapters.map(c => c.id))
  );
  const [newChapterTitle, setNewChapterTitle] = useState('');
  const [newSceneTitles, setNewSceneTitles] = useState<Record<number, string>>({});

  const toggleChapter = (id: number) => {
    setExpandedChapters(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const addChapter = async () => {
    if (!newChapterTitle.trim()) return;
    const fd = new FormData();
    fd.set('title', newChapterTitle.trim());
    await createChapter(arcId, fd);
    setNewChapterTitle('');
    router.refresh();
  };

  const addScene = async (chapterId: number) => {
    const title = newSceneTitles[chapterId]?.trim();
    if (!title) return;
    const fd = new FormData();
    fd.set('title', title);
    await createScene(String(chapterId), fd);
    setNewSceneTitles(prev => ({ ...prev, [chapterId]: '' }));
    router.refresh();
  };

  return (
    <div className="space-y-1">
      {arc.chapters.map(chapter => (
        <div key={chapter.id}>
          <button
            onClick={() => toggleChapter(chapter.id)}
            className="w-full flex items-center gap-2 p-2 hover:bg-slate-800 rounded-lg text-slate-300 text-sm transition-colors"
          >
            {expandedChapters.has(chapter.id)
              ? <ChevronDown size={14} className="shrink-0" />
              : <ChevronRight size={14} className="shrink-0" />}
            <span className="flex-1 text-left font-medium truncate">{chapter.title}</span>
          </button>

          {expandedChapters.has(chapter.id) && (
            <div className="ml-5 mt-0.5 space-y-0.5">
              {chapter.scenes.map(scene => (
                <button
                  key={scene.id}
                  onClick={() => router.push(`/storyboards/${arcId}?scene=${scene.id}`)}
                  className={`w-full text-left px-3 py-1.5 rounded-lg text-sm transition-colors ${
                    String(scene.id) === selectedSceneId
                      ? 'bg-purple-900/40 text-purple-300 border border-purple-500/30'
                      : 'hover:bg-slate-800 text-slate-400'
                  }`}
                >
                  {scene.title}
                </button>
              ))}

              {/* Add Scene inline */}
              <div className="flex gap-1 pt-1">
                <input
                  value={newSceneTitles[chapter.id] || ''}
                  onChange={e => setNewSceneTitles(prev => ({ ...prev, [chapter.id]: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter') addScene(chapter.id); }}
                  placeholder="New scene..."
                  className="flex-1 px-2 py-1 bg-slate-800 border border-slate-700 rounded text-xs text-slate-300 placeholder-slate-600 focus:outline-none focus:border-slate-600"
                />
                <button
                  onClick={() => addScene(chapter.id)}
                  className="p-1.5 bg-slate-700 hover:bg-slate-600 rounded transition-colors"
                  title="Add scene"
                >
                  <Plus size={12} className="text-slate-300" />
                </button>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Add Chapter */}
      <div className="flex gap-1 pt-3 mt-2 border-t border-slate-800">
        <input
          value={newChapterTitle}
          onChange={e => setNewChapterTitle(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') addChapter(); }}
          placeholder="New chapter..."
          className="flex-1 px-2 py-1 bg-slate-800 border border-slate-700 rounded text-xs text-slate-300 placeholder-slate-600 focus:outline-none focus:border-slate-600"
        />
        <button
          onClick={addChapter}
          className="p-1.5 bg-purple-700 hover:bg-purple-600 rounded transition-colors"
          title="Add chapter"
        >
          <Plus size={12} className="text-white" />
        </button>
      </div>
    </div>
  );
}
